/**
 * RESPONSIBILITY: one ministry's own page.
 *
 * Reached by row id, so every ministry has a page without any of them needing a section
 * of its own — adding one is a row in the Ministries list, not a deploy. An id that
 * matches nothing says so rather than showing a blank page.
 */
import { idOf } from '@framework/core';
import { Html } from '@framework/react';
import type { Ministry, EventRow, MinistryPageSection } from '@app/sections';
import { Picture } from '../commons/Picture';
import { MinistryBody } from './MinistryBody';
import { Crumbs } from '../commons/Crumbs';
import { EventCard } from '../events/EventCard';
import { Page, hrefFor } from '../commons/pages';
import { useWords } from '../commons/words';

export interface MinistryPageProps {
  m: Ministry | undefined;
  /** The words every ministry page shares. A ministry is a row and has nowhere to keep them. */
  c: MinistryPageSection;
  events: EventRow[];
}

export function MinistryPage({ m, c, events }: MinistryPageProps) {
  const w = useWords();

  if (!m) {
    return (
      <>
        <Crumbs trail={[{ label: w.ministriesLabel, page: Page.Ministry }, { label: w.notFoundLabel }]} />
        <section className="band band-centre">
          <div className="wrap">
            <h2>{c.missingHeading}</h2>
            <p className="lede">{c.missingBody}</p>
            <div className="btns btns-centre">
              <a className="btn" href={hrefFor(Page.Ministry)}>{c.missingCtaLabel}</a>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Crumbs trail={[{ label: w.ministriesLabel, page: Page.Ministry }, { label: m.title }]} />

      <section className="band">
        <div className="wrap split">
          <div className="stack">
            <h1>{m.title}</h1>
            {m.blurb && <p className="lede">{m.blurb}</p>}
          </div>
          <Picture value={m.heroImage?.path ? m.heroImage : m.image} alt="" className="block-pic" />
        </div>
      </section>

      {/* The description on its own, for a ministry that has not been given bands. */}
      {m.bodyHtml && (
        <section className="band band-tint">
          <div className="wrap"><Html className="md-body lede">{m.bodyHtml}</Html></div>
        </section>
      )}

      <MinistryBody bands={m.bands} />

      {events.length > 0 && (
        <section className="band">
          <div className="wrap">
            <div className="band-head">
              <h2>{c.upcomingHeading}</h2>
              <a className="btn btn-ghost" href={c.allEventsHref || hrefFor(Page.Events)}>
                {c.allEventsLabel}
              </a>
            </div>
            <div className="grid grid-3">
              {events.map((e) => <EventCard key={idOf(e)} e={e} />)}
            </div>
          </div>
        </section>
      )}

      <section className="band band-sand band-centre">
        <div className="wrap">
          <h2>{c.involvedHeading}</h2>
          <div className="btns btns-centre">
            <a className="btn" href={c.involvedCtaHref || hrefFor(Page.Contact)}>
              {c.involvedCtaLabel}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
